/* eslint-disable react/no-unescaped-entities */
import { yupResolver } from '@hookform/resolvers/yup';
import { CheckIcon, XIcon } from '@phosphor-icons/react';
import {
  Flex,
  message, Modal
} from 'antd';
import React, { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Controller, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { Typography } from 'tfc-components';

import Button from 'components/atoms/Button';
import InputCustom from 'components/atoms/InputCustom';
import { requestFreeTrialService } from 'services/systems';
import { IRequestFreeTrial } from 'services/systems/types';
import ERROR_MESSAGES, { DEFAULT_ERROR } from 'utils/errors';
import mapModifiers from 'utils/functions';
import { freeTrialSchema } from 'utils/schemas';

interface FreeTrialProps {
  isOpen: boolean;
  onClose: () => void;
}

const FreeTrial: React.FC<FreeTrialProps> = ({ isOpen, onClose }) => {
  const {
    control,
    formState: { errors, isValid },
    setError,
    trigger,
    handleSubmit,
    reset,
  } = useForm<IRequestFreeTrial>({
    defaultValues: {
      fullName: '',
      email: '',
      phone: '',
    },
    resolver: yupResolver(freeTrialSchema),
    mode: 'onChange',
  });

  // const [messageApi, contextHolder] = message.useMessage();
  const [successOpen, setSuccessOpen] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const recaptchaRef = useRef<ReCAPTCHA | null>(null);

  React.useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const handleSuccessClose = () => {
    setSuccessOpen(false);
  };

  // const success = (contentString: string) => {
  //   messageApi.open({
  //     type: 'success',
  //     content: contentString,
  //     className: 'p-home_message-success',
  //   });
  // };

  const { mutate: requestDemo } = useMutation({
    mutationKey: ['requestDemo'],
    mutationFn: requestFreeTrialService,
    onSuccess: (_, variables) => {
      // success('Request free trial successfully');
      setSubmittedEmail(variables.email);
      onClose();
      setSuccessOpen(true);
    },
    onError: (error) => {
      if (Array.isArray(error)) {
        error.forEach((err) => {
          if (err.field === 'packageFreeTrial') {
            message.error(ERROR_MESSAGES[err.code] || DEFAULT_ERROR);
          } else {
            setError(err.field, {
              message: err.message,
            });
          }
        });
      } else {
        message.error(DEFAULT_ERROR);
      }
    },
  });

  const onSubmit = async (data: IRequestFreeTrial) => {
    if (!recaptchaRef.current) return;
    const valid = await trigger();
    if (!valid) return;

    try {
      const token = await recaptchaRef.current.executeAsync();
      if (!token) return;
      requestDemo({ ...data, ggRecaptchaToken: token });
    } catch (error) {
      message.error(DEFAULT_ERROR);
    } finally {
      recaptchaRef.current.reset();
    }
  };

  const hasErrors = !!errors.fullName || !!errors.phone || !!errors.email;
  return (
    <>
      {/* Form Modal */}
      <Modal
        open={isOpen}
        onCancel={onClose}
        footer={(
          <Button
            disabled={hasErrors || !isValid}
            className="p-home_demo_submit"
            onClick={handleSubmit(onSubmit)}
          >
            Send
          </Button>
        )}
        centered
        width={915}
        title={(
          <Flex vertical gap={4} className="">
            <Typography.Text fontweight="600" extendClasses="fs-16x20 p-home_formHeading">
              Get your free account
            </Typography.Text>
            <Typography.Text extendClasses="p-home_demoFormText">
              Fill in your details below to receive your free account.
              We'll send your login information to the email provided.
            </Typography.Text>
          </Flex>
        )}
        className="p-home_modal"
      >
        <div className="p-home_demo">
          <form onSubmit={handleSubmit(onSubmit)} className="">
            <div className="p-pricingPlan_freeTrial_formLayout">
              <div className="p-pricingPlan_freeTrial_formSpace p-home_demo_formGroup">
                <Typography.Text extendClasses="p-home_demo_formLabel" fontweight="600">
                  Full name
                  {' '}
                  <span className="p-home_demo_quotes">*</span>
                </Typography.Text>
                <Controller
                  name="fullName"
                  control={control}
                  render={({ field, fieldState: { error: fullNameError } }) => (
                    <>
                      <InputCustom
                        {...field}
                        className="p-home_demo_formInput"
                        placeholder="Enter your name
"
                      />
                      {fullNameError && (
                        <Typography.Text extendClasses="p-pricingPlan_freeTrial_error">{fullNameError.message}</Typography.Text>
                      )}
                    </>
                  )}
                />
              </div>
              <div className="p-pricingPlan_freeTrial_formSpace p-home_demo_formGroup">
                <Typography.Text extendClasses="p-home_demo_formLabel" fontweight="600">
                  Phone number
                  {' '}
                  <span className="p-home_demo_quotes">*</span>
                </Typography.Text>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <>
                      <InputCustom
                        onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                          const { value } = e.currentTarget;

                          if (/[0-9]/.test(e.key)) return;

                          if (e.key === '+' && value.length === 0 && !value.includes('+')) {
                            return;
                          }

                          e.preventDefault();
                        }}
                        onPaste={(e) => {
                          const pasted = e.clipboardData.getData('text');
                          if (!/^[0-9+]+$/.test(pasted.replace(/^\+84/, '0'))) {
                            e.preventDefault();
                          }
                        }}
                        {...field}
                        inputMode="numeric"
                        className="p-home_demo_formInput"
                        placeholder="Enter your number"
                      />
                      {errors.phone && (
                        <Typography.Text extendClasses="p-pricingPlan_freeTrial_error">{errors.phone.message}</Typography.Text>
                      )}
                    </>
                  )}
                />
              </div>
            </div>
            <div className="p-home_demo_formLayout">
              <div className="p-home_demo_formGroupRow">
                <Typography.Text extendClasses="p-home_demo_formLabel" fontweight="600">
                  Email
                  {' '}
                  <span className="p-home_demo_quotes">*</span>
                </Typography.Text>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <>
                      <InputCustom
                        {...field}
                        placeholder="Enter your email"
                      />
                      {errors.email && (
                        <Typography.Text extendClasses="p-home_demo_error">
                          {errors.email.message}
                        </Typography.Text>
                      )}
                    </>
                  )}
                />
              </div>
            </div>
          </form>
        </div>
        <ReCAPTCHA ref={recaptchaRef} sitekey="6Lcp4MIZAAAAABHdGwULZhEv01tu2iODEDzSterA" size="invisible" />
      </Modal>

      {/* Success Modal */}
      <Modal
        open={successOpen}
        onCancel={handleSuccessClose}
        footer={null}
        width={640}
        title={null}
        className={mapModifiers('p-home_modal', 'success')}
        closable={false}
      >
        <div className="p-home_demo">
          <div className="p-pricingPlan_freeTrial_alert">
            <div className="p-pricingPlan_freeTrial_alert_icon">
              <CheckIcon size={32} color="#12B76A" />
            </div>
            <div className="p-pricingPlan_freeTrial_alert_content">
              <Typography.Text fontweight="600" extendClasses="p-pricingPlan_freeTrial_alert_title fs-18x21">
                Free trial activated!
              </Typography.Text>
              <Typography.Text extendClasses="p-pricingPlan_freeTrial_alert_description">
                You've successfully signed up for your free trial.
                <br />
                We've sent your account details to your email:
                {' '}
                <span className="p-pricingPlan_freeTrial_alert_email">
                  {submittedEmail}
                </span>
                <br />
                Enjoy exploring all the features!
              </Typography.Text>
            </div>
            <div className="p-pricingPlan_freeTrial_alert_close" onClick={handleSuccessClose}>
              <XIcon size={16} />
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default FreeTrial;
